import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { parseGuardianCode } from '../lib/supabase';
import { createChildAccount } from '../services/guardians';
import {
  H1,
  P,
  Button,
  Page,
  TextField,
  Card,
  CardContent,
  Stack,
  Alert,
  ChildrenSection,
} from '../components';
import {
  Box,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, ChildCare } from '@mui/icons-material';

function GuardianPage() {
  const { user, userType, signOut } = useAuth();
  const [username, setUsername] = useState('');
  const [schoolYear, setSchoolYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const guardianCode = user?.email ? parseGuardianCode(user.email) : '';

  const handleSignOut = async () => {
    await signOut();
  };

  const handleCreateChild = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!username.trim()) {
      setError('Username is required');
      setLoading(false);
      return;
    }

    if (schoolYear === '') {
      setError('School year is required');
      setLoading(false);
      return;
    }

    // Validate username format (basic validation)
    const usernamePattern = /^[a-zA-Z0-9_]+$/;
    if (!usernamePattern.test(username.trim())) {
      setError('Username can only contain letters, numbers, and underscores');
      setLoading(false);
      return;
    }

    try {
      const { error } = await createChildAccount(username, Number(schoolYear));

      if (error) {
        setError(error.message || 'Failed to create child account');
      } else {
        setSuccess(
          `Child account created successfully for "${username}"! The child can now log in using their username, password, and your guardian code.`
        );
        setUsername('');
        setSchoolYear('');
        setShowCreateForm(false);
        // Trigger refresh of children list
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (err) {
      setError('An unexpected error occurred while creating the child account');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setUsername('');
    setSchoolYear('');
    setError('');
    setSuccess('');
    setShowCreateForm(false);
  };

  return (
    <Page maxWidth="md" fullHeight>
      <Box sx={{ py: 4 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={3}
          sx={{ textAlign: 'center', mb: 4 }}
        >
          <H1>Guardian Dashboard</H1>
          <P>
            Welcome back! Manage your children&apos;s learning accounts below.
          </P>

          {user && (
            <Box
              sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}
            >
              <P>
                <strong>Email:</strong> {user.email}
              </P>
              <P>
                <strong>User Type:</strong> {userType}
              </P>
              {guardianCode && (
                <P>
                  <strong>Guardian Code:</strong> {guardianCode}
                </P>
              )}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Children List Section */}
        <ChildrenSection refreshTrigger={refreshTrigger} />

        <Divider sx={{ my: 3 }} />

        {/* Child Account Management Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <ChildCare color="secondary" />
            Child Account Management
          </Typography>

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {!showCreateForm ? (
            <Card elevation={1}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <ChildCare
                  sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }}
                />
                <Typography variant="h6" gutterBottom>
                  Create a New Child Account
                </Typography>
                <P color="text.secondary" sx={{ mb: 3 }}>
                  Add a new learning account for your child. They&apos;ll be
                  able to log in using their username, a password, and your
                  guardian code.
                </P>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Add />}
                  onClick={() => setShowCreateForm(true)}
                  size="large"
                >
                  Create Child Account
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Create New Child Account
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleCreateChild}>
                  <Stack spacing={3}>
                    <TextField
                      label="Child Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      fullWidth
                      disabled={loading}
                      placeholder="Enter username (e.g., daisy)"
                      helperText="Use only letters, numbers, and underscores. No spaces or special characters."
                    />

                    <FormControl fullWidth required disabled={loading}>
                      <InputLabel id="school-year-label">
                        School Year
                      </InputLabel>
                      <Select
                        labelId="school-year-label"
                        id="school-year-select"
                        value={schoolYear}
                        label="School Year"
                        onChange={(e) => setSchoolYear(e.target.value)}
                      >
                        <MenuItem value={0}>Reception</MenuItem>
                        <MenuItem value={1}>Year 1</MenuItem>
                        <MenuItem value={2}>Year 2</MenuItem>
                        <MenuItem value={3}>Year 3</MenuItem>
                        <MenuItem value={4}>Year 4</MenuItem>
                        <MenuItem value={5}>Year 5</MenuItem>
                        <MenuItem value={6}>Year 6</MenuItem>
                      </Select>
                    </FormControl>

                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: 'info.light',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" color="info.contrastText">
                        <strong>📝 Note:</strong> After creating the account,
                        your child will need:
                        <br />• Their username:{' '}
                        <strong>{username || '[username]'}</strong>
                        <br />• A password (set during account creation)
                        <br />• Your guardian code:{' '}
                        <strong>{guardianCode || '[your-code]'}</strong>
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={2}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={loading}
                        fullWidth
                      >
                        {loading ? 'Creating Account...' : 'Create Account'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={resetForm}
                        disabled={loading}
                        fullWidth
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Sign Out Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSignOut}
            size="large"
          >
            Sign Out
          </Button>
        </Box>
      </Box>
    </Page>
  );
}

export default GuardianPage;
